import userPath from './userPath.yml'

/**
 * Create the previous and next path for navigation between the screens
 *
 */
export class LinkedChainNavigation {
  public currentPath: string
  private readonly userPaths: string[]

  constructor(currentPath: string, userPaths: string[] = userPath) {
    this.userPaths = userPaths
    this.currentPath = currentPath
  }

  /**
   * Read the bounds from the user paths definition
   */
  get questionBounds(): [number, number] | undefined {
    // get the path with the questions
    const questionPath = this.userPaths.find(el => el.startsWith('/question'))

    if (questionPath != null) {
      const boundsMatch = /{(.+)}/.exec(questionPath)
      if (boundsMatch && boundsMatch[1]) {
        const boundStrings = boundsMatch[1].split('-')
        return [parseInt(boundStrings[0], 10), parseInt(boundStrings[1], 10)]
      } else {
        throw new RangeError(
          'Question Bounds has not been found in user paths definition.'
        )
      }
    } else {
      // we have no information about the question bounds, since we did not
      // find any data in the user paths, so it is (logically) undefined.
      return undefined
    }
  }

  private getPath(direction: number): string | undefined {
    const nextPath = this.userPaths[
      this.userPaths.indexOf(this.currentPath) + direction
    ]

    // special case 1: we're doing questions

    if (this.currentPath.startsWith('/question')) {
      const result = /\d+/.exec(this.currentPath)
      if (result) {
        const questionIndex = parseInt(result[0], 10)
        if (
          this.questionBounds &&
          questionIndex === this.questionBounds[direction > 0 ? 1 : 0]
        ) {
          const idx = this.userPaths.findIndex(el => el.startsWith('/question'))
          return this.userPaths[idx + direction]
        } else {
          return '/question/' + (questionIndex + direction)
        }
      }
    }

    // special case: the next/previous path is a /question path
    if (nextPath && nextPath.startsWith('/question') && this.questionBounds) {
      return '/question/' + this.questionBounds[direction > 0 ? 0 : 1]
    }

    return nextPath
  }

  /**
   * Get the next path in the navigation list
   */
  public get next(): string | undefined {
    return this.getPath(1)
  }

  /**
   * Get the previous path in the navigation list
   */
  public get previous(): string | undefined {
    return this.getPath(-1)
  }

  /**
   * Get the length of the complete navigation list, including all /questions.
   * Used to calculate the overall length of the questionnaire/interactive.
   */
  public get length(): number {
    return (
      this.userPaths.length +
      (this.questionBounds
        ? this.questionBounds[1] - this.questionBounds[0]
        : 0)
    )
  }

  private getIndexOfPath(path: string): number | undefined {
    const unexplodedIndex = this.userPaths.indexOf(path)
    const questionIndex = this.userPaths.findIndex(el =>
      el.startsWith('/question')
    )
    const isQuestionPath = path.startsWith('/question')

    // trivial case: there are no questions
    if (
      questionIndex === -1 ||
      (!isQuestionPath && unexplodedIndex < questionIndex)
    ) {
      return unexplodedIndex
    }

    if (isQuestionPath) {
      const matches = /\d+/.exec(this.currentPath)
      if (matches && this.questionBounds) {
        return (
          questionIndex + (parseInt(matches[0], 10) - this.questionBounds[0])
        )
      }
    }

    if (unexplodedIndex > questionIndex && this.questionBounds) {
      return unexplodedIndex + this.questionBounds[1] - this.questionBounds[0]
    }
  }

  /**
   * Get the index of the current path
   */
  public get currentIndex(): number | undefined {
    return this.getIndexOfPath(this.currentPath)
  }
}
